// Post-take QC feedback messages.
//
// Keyed by `${check_id}_${severity}`. Each entry explains WHY the check
// flagged, in conversational tone. Severity = 'fail' is the only path that
// gets red styling (per tone rule #1) — true clipping or unrecoverable noise.
/**
 * QC feedback shown after a take is auto-analysed.
 *
 * Conventions:
 *   - `pass_general` is the universal "looked good" message.
 *   - For each check_id, `*_pass` is OMITTED (the take is silently accepted).
 *   - `*_warn` and `*_fail` always state the metric AND a suggested fix.
 *   - The word "AI" is avoided in body copy — we say "参考声" / "voz de
 *     referencia" / "reference voice".
 */
export const QC = {
    pass_general: {
        icon: '✅',
        zh: '这条录得很好。',
        en: 'Nice take.',
        es: 'Buena toma.',
    },
    // ── clipping ───────────────────────────────────────────────────────────
    clipping_warn: {
        icon: '📈',
        zh: '峰值轻微触顶。下一条把增益降 3 dB 应该就稳了。',
        en: 'Peaks brushed the ceiling. Drop the gain 3 dB on the next take and you are clear.',
        es: 'Los picos rozaron el techo. Baje la ganancia 3 dB en la siguiente toma y quedará despejado.',
    },
    clipping_fail: {
        icon: '🔴',
        zh: '出现削顶 X 处。可能是上一条喊叫的余波。再来一遍?',
        en: 'Clipping detected at X samples. Likely leftover gain from the previous shout. Try again?',
        es: 'Se detectó saturación en X muestras. Probablemente es ganancia residual del grito anterior. ¿Lo repetimos?',
    },
    // ── noise floor ────────────────────────────────────────────────────────
    noisefloor_warn: {
        icon: '🌬️',
        zh: '环境噪声偏高(底噪 -55 dBFS)。检查空调或风扇。',
        en: 'Background noise is a bit high (-55 dBFS). Check your AC / fan.',
        es: 'El ruido de fondo está algo alto (-55 dBFS). Revise el aire acondicionado o el ventilador.',
    },
    noisefloor_fail: {
        icon: '🔴',
        zh: '底噪太高(-42 dBFS),清理后重录这条。',
        en: 'Noise floor is too hot (-42 dBFS). Quiet the room and re-record this one.',
        es: 'El ruido de fondo es demasiado alto (-42 dBFS). Silencie la sala y vuelva a grabar esta toma.',
    },
    // ── length variance (informational only here; lengthPrompt is the real UX) ─
    length_warn: {
        icon: '⏱️',
        zh: '时长和参考声有点偏差。看一眼下面的对比。',
        en: 'Length is a touch off versus the reference. See the comparison below.',
        es: 'La duración difiere ligeramente respecto a la voz de referencia. Vea la comparación abajo.',
    },
    length_fail: {
        icon: '⏱️',
        zh: '时长差超过容许范围。请选择重录或保留。',
        en: 'Length is outside the tolerance window. Choose re-record or keep.',
        es: 'La duración está fuera del margen permitido. Elija repetir la toma o conservarla.',
    },
    // ── misread ────────────────────────────────────────────────────────────
    misread_warn: {
        icon: '📝',
        zh: '可能漏字或读错了一个词,确认一下?',
        en: 'Possible word slip or misread. Want to double-check?',
        es: 'Posible lapsus o palabra mal leída. ¿Quiere verificarlo?',
    },
    misread_fail: {
        icon: '🔴',
        zh: '台词没对上原稿。再来一遍。',
        en: 'The take does not match the script. Let us run it again.',
        es: 'La toma no coincide con el guion. Vamos a repetirla.',
    },
    // ── plosive ────────────────────────────────────────────────────────────
    plosive_warn: {
        icon: '💨',
        zh: '听到一个爆破音(p / b)。挪一指宽离 mic 应该就好。',
        en: 'Heard a plosive on a "p" or "b". A finger-width off-axis usually fixes it.',
        es: 'Se escucha una plosiva en una "p" o "b". Desviarse un dedo del eje del micrófono suele resolverlo.',
    },
    plosive_fail: {
        icon: '💨',
        zh: '爆破音明显。建议加防风罩或侧 30° 对 mic。',
        en: 'Plosive is loud enough to keep. Add a pop filter or angle 30° off-axis.',
        es: 'La plosiva es lo bastante fuerte como para quedar grabada. Añada un antipop o angule el micrófono 30° fuera de eje.',
    },
    // ── sibilance ──────────────────────────────────────────────────────────
    sibilance_warn: {
        icon: '🦷',
        zh: '齿音(s / sh)有点尖。试着稍微远一点 mic。',
        en: 'Sibilants are a little sharp. A small step back from the mic should soften them.',
        es: 'Las sibilantes suenan algo agudas. Aléjese un poco del micrófono para suavizarlas.',
    },
    // ── leading / trailing silence ─────────────────────────────────────────
    silence_head_warn: {
        icon: '⏪',
        zh: '开头静音偏长(800 ms)。可以裁,但下一条早点起来更稳。',
        en: 'Long silence at the head (800 ms). Trimmable, but try entering earlier next time.',
        es: 'Silencio largo al inicio (800 ms). Es recortable, pero la próxima vez intente entrar antes.',
    },
    silence_tail_warn: {
        icon: '⏩',
        zh: '结尾静音偏长。下一条录完早点松 Space。',
        en: 'Trailing silence is long. Release Space sooner after the last word.',
        es: 'El silencio final es largo. Suelte Espacio antes tras la última palabra.',
    },
    // ── mouth noise / clicks ───────────────────────────────────────────────
    mouth_noise_warn: {
        icon: '👄',
        zh: '听到一处口水音/click。要不要补录?',
        en: 'Caught a mouth click. Want to do a quick pickup?',
        es: 'Se detectó un chasquido bucal. ¿Quiere hacer un pickup rápido?',
    },
    // ── room tone presence ─────────────────────────────────────────────────
    room_tone_warn: {
        icon: '🏠',
        zh: '房间反射比平时多一点。门关上或挂条毯子会更干。',
        en: 'A bit more room reflection than usual. Closing a door or hanging a blanket helps.',
        es: 'Hay algo más de reflexión de sala de lo habitual. Cerrar una puerta o colgar una manta ayuda.',
    },
};
// ── Project-level QC threshold form copy ────────────────────────────────────
// Used by QcThresholdsForm.tsx in the producer setup tab. Producer-facing copy
// (tone is a touch more technical than the actor surface).
export const THRESHOLDS_TITLE = {
    zh: 'QC 阈值',
    en: 'QC thresholds',
    es: 'Umbrales de QC',
};
export const THRESHOLDS_INTRO = {
    zh: '调整以下阈值会改变 take 自动检查时通过、警告与失败的边界。每个项目可独立配置。',
    en: 'These thresholds set the boundary between pass, warn, and fail when each take is auto-checked. Configurable per project.',
    es: 'Estos umbrales definen los límites entre aprobado, aviso y fallo cuando cada toma se analiza automáticamente. Configurable por proyecto.',
};
export const THRESHOLDS_CLIPPING = {
    zh: '削顶阈值 (dBFS)',
    en: 'Clipping threshold (dBFS)',
    es: 'Umbral de saturación (dBFS)',
};
export const THRESHOLDS_CLIPPING_HELP = {
    zh: '高于此电平的样本会被判为削顶。一般在 -1 到 -0.3 之间。',
    en: 'Samples above this level are flagged as clipped. Typical: -1 to -0.3 dBFS.',
    es: 'Las muestras por encima de este nivel se marcan como saturadas. Típico: -1 a -0.3 dBFS.',
};
export const THRESHOLDS_NOISEFLOOR = {
    zh: '底噪上限 (dBFS)',
    en: 'Noise floor max (dBFS)',
    es: 'Ruido de fondo máximo (dBFS)',
};
export const THRESHOLDS_NOISEFLOOR_HELP = {
    zh: '底噪高于此值会触发警告。专业家庭录音棚通常 ≤ -60 dBFS。',
    en: 'Background noise above this triggers a warning. Pro home booths typically ≤ -60 dBFS.',
    es: 'Un ruido de fondo superior a este valor genera un aviso. Las cabinas profesionales caseras suelen estar ≤ -60 dBFS.',
};
export const THRESHOLDS_LENGTH = {
    zh: '时长偏差上限 (%)',
    en: 'Length variance max (%)',
    es: 'Variación máxima de duración (%)',
};
export const THRESHOLDS_LENGTH_HELP = {
    zh: '当 take 时长与参考声偏差超过该百分比时,弹出二选一提示。',
    en: 'When a take deviates from the reference by more than this percent, the variance prompt fires.',
    es: 'Cuando una toma se desvía de la referencia por encima de este porcentaje, aparece el aviso de variación.',
};
export const THRESHOLDS_MISREAD_STRICT = {
    zh: '严格漏字检测',
    en: 'Strict misread detection',
    es: 'Detección estricta de lapsus',
};
export const THRESHOLDS_MISREAD_STRICT_HELP = {
    zh: '开启后,任何与原稿不一致的字词都会被判失败而不是警告。',
    en: 'When on, any deviation from the script is treated as a failure rather than a warning.',
    es: 'Si está activado, cualquier diferencia con el guion se considera fallo en lugar de aviso.',
};
export const THRESHOLDS_RESTORE_DEFAULTS = {
    zh: '恢复默认值',
    en: 'Restore defaults',
    es: 'Restablecer valores predeterminados',
};
export const THRESHOLDS_SAVE = {
    zh: '保存',
    en: 'Save',
    es: 'Guardar',
};
export const THRESHOLDS_SAVED = {
    zh: '阈值已保存。',
    en: 'Thresholds saved.',
    es: 'Umbrales guardados.',
};
export const THRESHOLDS_SAVE_FAILED = {
    zh: '保存失败,请重试。',
    en: 'Save failed — try again.',
    es: 'Error al guardar — inténtelo de nuevo.',
};
/** Defaults used by QcThresholdsForm's "Restore defaults" button. */
export const DEFAULT_QC_THRESHOLDS = {
    clipping_threshold_db: -0.3,
    noise_floor_max_db: -60,
    length_variance_pct: 15,
    misread_strict: false,
};
